"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  content: string | React.ReactNode | null;
  children: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  className?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = "top",
  className = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [positionReady, setPositionReady] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [currentPosition, setCurrentPosition] = useState(position);
  const targetRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalElement(document.body);
  }, []);

  const updatePosition = useCallback(() => {
    if (!targetRef.current || !tooltipRef.current) return;

    const targetRect = targetRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const { innerWidth, innerHeight } = window;

    // Skip if tooltip not laid out yet (avoids wrong position on first show)
    if (tooltipRect.width === 0 && tooltipRect.height === 0) return;

    const GAP = 8;
    let newPosition = position;

    // Use viewport coordinates only — tooltip uses position: fixed
    const pos = {
      top: {
        top: targetRect.top - tooltipRect.height - GAP,
        left: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
      },
      bottom: {
        top: targetRect.bottom + GAP,
        left: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
      },
      left: {
        top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
        left: targetRect.left - tooltipRect.width - GAP,
      },
      right: {
        top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
        left: targetRect.right + GAP,
      },
    };

    const checks = {
      top: () => pos.top.top < 0,
      bottom: () => pos.bottom.top + tooltipRect.height > innerHeight,
      left: () => pos.left.left < 0,
      right: () => pos.right.left + tooltipRect.width > innerWidth,
    };

    if (checks[newPosition]()) {
      const opposites = {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
      };
      newPosition = opposites[newPosition] as typeof position;
    }

    let { top, left } = pos[newPosition];

    // Clamp to viewport (fixed = viewport-relative)
    if (newPosition === "top" || newPosition === "bottom") {
      left = Math.max(GAP, Math.min(left, innerWidth - tooltipRect.width - GAP));
    } else {
      top = Math.max(GAP, Math.min(top, innerHeight - tooltipRect.height - GAP));
    }

    setTooltipPosition({ top, left });
    setCurrentPosition(newPosition);
  }, [position]);

  useEffect(() => {
    if (isVisible) {
      setPositionReady(false);
      // Defer so tooltip is laid out before we measure (avoids 0x0 rect and wrong placement)
      const rafId = requestAnimationFrame(() => {
        updatePosition();
        requestAnimationFrame(() => {
          updatePosition();
          setPositionReady(true);
        });
      });
      const handleScroll = () => updatePosition();
      window.addEventListener("scroll", handleScroll, { passive: true });
      window.addEventListener("resize", handleScroll);
      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleScroll);
        setPositionReady(false);
      };
    }
  }, [isVisible, updatePosition]);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const tooltipClasses = `
    fixed z-99999999 px-3 py-2 text-sm font-medium text-text bg-light-2 rounded-lg shadow-lg
    pointer-events-none transition-opacity duration-200 ${isVisible && positionReady ? "opacity-100" : "opacity-0"}
    ${className}
  `;

  const tooltipContent =
    isVisible && portalElement && content
      ? createPortal(
          <div
            ref={tooltipRef}
            className={tooltipClasses}
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            {content}
            <div
              className={`
          absolute w-2 h-2 bg-light-2 transform rotate-45
          ${currentPosition === "bottom" ? "-top-1 left-1/2 -translate-x-1/2" : ""}
          ${currentPosition === "top" ? "-bottom-1 left-1/2 -translate-x-1/2" : ""}
          ${currentPosition === "left" ? "top-1/2 -right-1 -translate-y-1/2" : ""}
          ${currentPosition === "right" ? "top-1/2 -left-1 -translate-y-1/2" : ""}
        `}
            />
          </div>,
          portalElement
        )
      : null;

  return (
    <div
      ref={targetRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="inline-block"
    >
      {children}
      {tooltipContent}
    </div>
  );
};

export default Tooltip;
